/**
 * PATH: packages/job-hub/src/jobs/qa/QaParticipantsEditor.tsx
 * FILE_ID: JOBHUB-QA-PARTICIPANTS-EDITOR-001
 * PURPOSE: Presentational participants list with searchable add and remove.
 */
import { useState } from 'react'
import { X } from 'lucide-react'
import ComboField from '@quotech/quote-builder/components/ComboField'
import { QA_PARTICIPANT_ROLES, qaRoleLabel } from './qaParticipantRoles'

export interface QaParticipantPerson {
  user_id: number
  name: string
  email: string
}

export interface QaParticipantRow {
  key: string
  name: string
  email: string
  role: string
  kind?: 'participant' | 'team'
  userId?: number
}

export function QaParticipantsEditor(props: {
  rows: QaParticipantRow[]
  search: (query: string) => Promise<QaParticipantPerson[]>
  onAdd: (person: QaParticipantPerson, role: string) => Promise<void> | void
  onRemove: (row: QaParticipantRow) => Promise<void> | void
  busy?: boolean
  error?: string | null
}) {
  const [picked, setPicked] = useState<QaParticipantPerson | null>(null)
  const [role, setRole] = useState('supervisor')
  const [comboKey, setComboKey] = useState(0)
  const [adding, setAdding] = useState(false)
  const [emptyLabel, setEmptyLabel] = useState('No people match')

  const add = async () => {
    if (!picked || props.busy || adding) return
    setAdding(true)
    try {
      await props.onAdd(picked, role)
      setPicked(null)
      setComboKey((value) => value + 1)
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="grid min-w-0 gap-2">
      <span className="text-sm">Participants</span>
      <ul className="grid gap-1 text-xs">
        {props.rows.map((row) => (
          <li key={row.key} className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate">
              {[row.name, row.email, qaRoleLabel(row.role)].filter(Boolean).join(' · ')}
            </span>
            <button
              type="button"
              aria-label={`Remove ${row.name || row.email}`}
              disabled={props.busy}
              onClick={() => { void props.onRemove(row) }}
              className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-col items-stretch gap-2 min-[360px]:flex-row min-[360px]:items-center">
        <div data-testid="qa-participant-search" className="relative min-w-0 flex-1">
          <ComboField<QaParticipantPerson>
            key={comboKey}
            label=""
            value={picked ? (picked.name || picked.email) : ''}
            placeholder="Search by name or email"
            minChars={1}
            search={async (query) => {
              try {
                const found = await props.search(query)
                setEmptyLabel('No people match')
                return found
              } catch (err) {
                const message = err instanceof Error && err.message.startsWith('Search failed:')
                  ? err.message
                  : 'Search failed: error'
                setEmptyLabel(message)
                return []
              }
            }}
            getKey={(person) => person.user_id}
            getDisplay={(person) => person.name || person.email}
            renderItem={(person) => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{person.name || person.email}</span>
                {person.email ? (
                  <span className="truncate text-xs text-muted-foreground">{person.email}</span>
                ) : null}
              </span>
            )}
            onSelect={setPicked}
            onCommitText={() => undefined}
            portal
            emptyLabel={emptyLabel}
            inputClassName="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-8 text-sm"
          />
        </div>
        <select
          aria-label="Participant role"
          className="w-full shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-sm min-[360px]:w-40"
          value={role}
          onChange={(event) => setRole(event.target.value)}
        >
          {QA_PARTICIPANT_ROLES.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={!picked || Boolean(props.busy) || adding}
          onClick={() => { void add() }}
          className="shrink-0 rounded-lg border border-border px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {props.error ? (
        <p role="alert" className="text-xs text-destructive">{props.error}</p>
      ) : null}
    </div>
  )
}
